import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Sidebar } from "../sidebar/Sidebar";
import { ReactComponent as ArtworkIcon } from "../icons/ArtworkIcon.svg";
import PropTypes from "prop-types";
import {
  Card,
  CardHeader,
  CardPreview,
  Checkbox,
  Button,
  SearchBox,
  Dropdown,
  Option,
  Label,
  Skeleton, // Add Skeleton to the imports
  SkeletonItem,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuItem,
  MenuList,
  TabList,
  Tab,
  Divider,
  Spinner
} from "@fluentui/react-components";
import {
  ArrowLeft24Filled,
  ArrowRight24Filled,
  CubeMultipleFilled,
  Delete20Filled,
  DocumentPdf16Filled,
  DocumentPdfFilled,
  Edit20Filled,
  Image16Filled,
  ImageFilled,
  ArrowSync20Filled,
  MoreVertical20Filled,
  Rename20Filled,
  Speaker216Filled,
  Speaker2Filled,
  Video16Filled,
  VideoFilled
} from "@fluentui/react-icons";
import { makeStyles, tokens } from "@fluentui/react-components";
import { RenameAssetDialog } from "./AssetsRenameModal";
import { DeleteAssetDialog } from "./AssetsDeleteModal";
import { useDrag } from "react-dnd";
import useBreakpoints from "../hooks/useBreakpoints";
import { useWorkspaceContext } from "../contexts/WorkspaceContext";
import { isWorkspaceSelectable } from "../contexts/workspace-selectable-utils";
import { shouldShowMenuTrigger } from "./utils/library-acquire-utils";

const useStyles = makeStyles({
  sidebar: {
    width: "100vw", // Full width on mobile
    "@media (min-width: 500px)": {
      width: "500px!important"
    }
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: tokens.spacingVerticalMNudge,
    "@media (min-width: 500px)": {
      gridTemplateColumns: "1fr 1fr" // Two columns on larger screens
    }
  },
  card: {
    width: "100%", // Full width of the grid cell
    marginBottom: tokens.spacingVerticalS,
    backgroundColor: "var(--secondaryColorBackground)"
  },
  cardPreview: {
    background:
      "linear-gradient(90deg, var(--primaryColorBackground) 0%, var(--secondaryColorBackground) 50%, var(--primaryColorBackground) 100%)",
    height: "200px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& > img": {
      objectFit: "cover",
      height: "100%",
      width: "100%",
      transition: "object-fit 0.3s ease, transform 0.3s ease", // Smooth transition
      transform: "scale(1)" // Initial scale
    },

    // Hover effect
    ":hover > img": {
      objectFit: "contain",
      transform: "scale(0.95)" // Scale down slightly for zoom-out effect
    },

    "& .fui-CardPreview__logo": {
      top: "12px",
      bottom: "unset",
      width: "24px",
      height: "24px"
    }
  },
  smallRadius: {
    borderRadius: tokens.borderRadiusSmall
  },
  caption: {
    color: tokens.colorNeutralForeground3
  },
  headerTitle: {
    "& .fui-CardHeader__header": {
      margin: 0,
      fontSize: "16px",
      fontWeight: 600,
      display: "-webkit-box",
      maxWidth: "100%",
      WebkitLineClamp: 1,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis"
    }
  },
  logoBadge: {
    backgroundColor: "var(--primaryColorBackground)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    borderRadius: tokens.borderRadiusSmall
  },
  searchContainer: {},
  search: {
    width: "100%",
    marginBottom: tokens.spacingVerticalMNudge
  },
  fileTypeFilter: {
    width: "100%",
    minWidth: "0" // Allow shrinking below content size
  },
  fileTypeFilterLabel: {
    fontWeight: tokens.fontWeightRegular,
    color: "var(--Gray-3)",
    textTransform: "uppercase"
  },
  fileTypeFilterContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalL
  },
  filtersRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalMNudge,
    width: "100%"
  },
  filterColumn: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    minWidth: "0", // Allow shrinking below content size
    width: "100%",
    flex: "1 1 50%" // Each column takes 50% width
  },
  invertedWrapper: {
    backgroundColor: tokens.colorNeutralBackground1
  },
  firstRow: {
    alignItems: "center",
    display: "flex",
    padding: "10px",
    position: "relative",
    gap: "10px",
    justifyContent: "space-between"
  },
  secondThirdRow: {
    alignItems: "center",
    display: "grid",
    paddingBottom: "10px",
    position: "relative",
    gap: "10px",
    gridTemplateColumns: "min-content 20% 20% 15% 15%"
  },
  footer: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  tabListContainer: {
    marginBottom: tokens.spacingVerticalMNudge
  },
  content: {
    paddingTop: tokens.spacingVerticalMNudge,
    paddingBottom: tokens.spacingVerticalMNudge
  },
  pager: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: tokens.spacingVerticalMNudge
  },
  tab: {
    "&:before": {
      left: "0px!important",
      right: "0px!important"
    },
    "&:after": {
      left: "0px!important",
      right: "0px!important"
    }
  }
});

const ItemTypes = {
  ASSET: "asset"
};

const LogoBadge = ({ type, style }) => {
  const styles = useStyles();
  const iconMapping = {
    artwork: <ArtworkIcon styles={{ width: "16px", height: "16px" }} />,
    audio: <Speaker216Filled />,
    pdf: <DocumentPdf16Filled />,
    model: <CubeMultipleFilled style={{ width: "16px", height: "16px" }} />,
    video: <Video16Filled />,
    default: <Image16Filled />
  };

  return (
    <span className={styles.logoBadge} style={style}>
      {iconMapping[type] || iconMapping.default}
    </span>
  );
};

LogoBadge.propTypes = {
  type: PropTypes.string,
  style: PropTypes.object
};

const getPlaceholders = type => {
  const iconMapping = {
    artwork: <ArtworkIcon styles={{ width: "64px", height: "64px" }} />,
    audio: <Speaker2Filled style={{ width: "64px", height: "64px" }} />,
    pdf: <DocumentPdfFilled style={{ width: "64px", height: "64px" }} />,
    model: <CubeMultipleFilled style={{ width: "64px", height: "64px" }} />,
    video: <VideoFilled style={{ width: "64px", height: "64px" }} />,
    default: <ImageFilled style={{ width: "64px", height: "64px" }} />
  };

  return iconMapping[type] || iconMapping.default;
};

const MenuActions = ({
  item,
  editable,
  canDeleteAssets,
  handleRename,
  handleDelete,
  handleEdit,
  handleOptimize,
  canAddToLibrary,
  handleAddToLibrary
}) => {
  const canUpdate = editable && (item?.can ? item.can.update : true);
  const canDelete = canDeleteAssets && (item?.can ? item.can.delete : true);
  const showEdit = editable && canUpdate;
  const showRename = editable && canUpdate && item?.metadata?.editable !== false;
  const canOptimize = item?.type === "model" && canUpdate;

  const hasAnyAction = shouldShowMenuTrigger({
    canRename: showEdit || showRename,
    canDelete,
    canOptimize,
    canAddToLibrary
  });

  if (!hasAnyAction) return null;

  return (
    <Menu positioning={"below-end"}>
      <MenuTrigger>
        <Button
          appearance="transparent"
          icon={<MoreVertical20Filled />}
          aria-label={<FormattedMessage id="more-menu-popover.title" defaultMessage="More" />}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {canOptimize && (
            <MenuItem icon={<ArrowSync20Filled />} onClick={e => handleOptimize(e, item)}>
              <FormattedMessage id="object-menu.optimize" defaultMessage="Optimize" />
            </MenuItem>
          )}
          {showEdit && (
            <MenuItem icon={<Edit20Filled />} onClick={e => handleEdit(e, item)}>
              <FormattedMessage id="object-menu.edit" defaultMessage="Edit" />
            </MenuItem>
          )}
          {showRename && (
            <MenuItem icon={<Rename20Filled />} onClick={e => handleRename(e, item)}>
              <FormattedMessage id="object-menu.rename" defaultMessage="Rename" />
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem icon={<Delete20Filled />} onClick={e => handleDelete(e, item)}>
              <FormattedMessage id="object-menu.delete" defaultMessage="Delete" />
            </MenuItem>
          )}
          {canAddToLibrary && (
            <MenuItem icon={<Image16Filled />} onClick={e => handleAddToLibrary(e, item)}>
              <FormattedMessage id="asset-editor.add-to-library" defaultMessage="Add to library" />
            </MenuItem>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

MenuActions.propTypes = {
  item: PropTypes.object,
  editable: PropTypes.bool,
  canDeleteAssets: PropTypes.bool,
  handleRename: PropTypes.func,
  handleDelete: PropTypes.func,
  handleEdit: PropTypes.func,
  handleOptimize: PropTypes.func,
  canAddToLibrary: PropTypes.bool,
  handleAddToLibrary: PropTypes.func
};

const resolveAssetTypeStyle = type => {
  switch (type) {
    case "artwork":
      return { backgroundColor: "rgb(80, 163, 240)" };
    case "audio":
      return { backgroundColor: "rgb(218, 80, 240)" };
    case "pdf":
      return { backgroundColor: "rgb(10, 95, 100)" };
    case "model":
      return { backgroundColor: "rgb(85, 102, 195)" };
    case "video":
      return { backgroundColor: "rgb(95, 206, 142)" };
    default:
      return { backgroundColor: "rgb(151, 71, 255)" };
  }
};

const DraggableCard = ({
  item,
  index,
  selectedItems,
  handleSelectionChange,
  editable,
  canDeleteAssets,
  handleRename,
  handleDelete,
  handleEdit,
  handleOptimize,
  canAddToLibrary,
  handleAddToLibrary,
  processThumbnailUrl,
  failedImages,
  handleError,
  formattedSelectedItems,
  selectionDisabled
}) => {
  const styles = useStyles();

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ASSET,
    canDrag: !selectionDisabled,
    item: selectedItems[item.id]
      ? {
          selectedItems: formattedSelectedItems
        }
      : { selectedItems: [item] },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  const thumbnailUrl = processThumbnailUrl(item.thumbnail);

  return (
    <Card
      ref={drag}
      key={`asset-${item.id}-${index}`}
      className={styles.card}
      floatingAction={
        <Checkbox
          onChange={() => {
            if (!selectionDisabled) {
              handleSelectionChange(item.id);
            }
          }}
          disabled={selectionDisabled}
          checked={!!selectedItems[item.id]}
          aria-label={`Select asset ${item.name}`}
        />
      }
      selected={!!selectedItems[item.id]}
      onSelectionChange={() => {
        if (!selectionDisabled) {
          handleSelectionChange(item.id);
        }
      }}
      style={{ opacity: isDragging ? 0.5 : 1, cursor: selectionDisabled ? "default" : "move" }}
    >
      <CardPreview
        className={styles.cardPreview}
        logo={<LogoBadge type={item.type} style={resolveAssetTypeStyle(item.type)} />}
      >
        {thumbnailUrl && !failedImages[item.id] ? (
          <img
            className={styles.smallRadius}
            src={thumbnailUrl}
            alt={`${item.name} Thumbnail`}
            onError={e => handleError(e, item)}
          />
        ) : (
          getPlaceholders(item.type)
        )}
      </CardPreview>

      <CardHeader
        header={item.title || item.name}
        action={
          editable || canAddToLibrary ? (
            <MenuActions
              item={item}
              editable={editable}
              canDeleteAssets={canDeleteAssets}
              handleRename={(e, item) => handleRename(e, item)}
              handleDelete={(e, item) => handleDelete(e, item)}
              handleEdit={(e, item) => handleEdit(e, item)}
              handleOptimize={(e, item) => handleOptimize(e, item)}
              canAddToLibrary={canAddToLibrary}
              handleAddToLibrary={(e, item) => handleAddToLibrary(e, item)}
            />
          ) : null
        }
        className={styles.headerTitle}
      />
    </Card>
  );
};

DraggableCard.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  selectedItems: PropTypes.object.isRequired,
  handleSelectionChange: PropTypes.func.isRequired,
  editable: PropTypes.bool.isRequired,
  canDeleteAssets: PropTypes.bool,
  handleRename: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleOptimize: PropTypes.func.isRequired,
  canAddToLibrary: PropTypes.bool,
  handleAddToLibrary: PropTypes.func,
  processThumbnailUrl: PropTypes.func.isRequired,
  failedImages: PropTypes.object.isRequired,
  handleError: PropTypes.func.isRequired,
  formattedSelectedItems: PropTypes.array,
  selectionDisabled: PropTypes.bool
};

export function AssetsSidebar({
  children,
  open,
  onClose,
  items,
  loading,
  error,
  searchQuery,
  setSearchQuery,
  processThumbnailUrl,
  fileTypeFilter,
  handleFileTypeFilterChange,
  fileTypeOptions = [],
  assetCategoryFilter,
  handleAssetCategoryFilterChange,
  assetCategoryOptions = [],
  onSelectAssets,
  onRename,
  onDelete,
  onOptimize,
  onAddToLibrary,
  canAddToLibrary,
  tabs,
  selectedTab,
  handleSelectedTabChange,
  sourceOptions,
  handleSourceChange,
  source,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  editable,
  canDeleteAssets,
  placeLoading,
  initEditMode,
  openFileUploadDrawer,
  onClearEdit,
  selectionDisabled
}) {
  const styles = useStyles();
  const [selectedItems, setSelectedItems] = useState({});
  const [assetToRename, setAssetToRename] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(false);
  const [failedImages, setFailedImages] = useState({});
  const breakpoint = useBreakpoints();
  const isMobile = ["xs", "sm"].includes(breakpoint);
  const drawerBodyRef = useRef(null);
  const { workspaces, selectedWorkspace, setSelectedWorkspace } = useWorkspaceContext();

  // Cleanup edit state when tab changes
  useEffect(() => {
    if (onClearEdit) {
      onClearEdit();
    }
  }, [selectedTab, onClearEdit]);

  const scrollToTop = () => {
    if (drawerBodyRef.current) {
      drawerBodyRef.current.scrollTop = 0;
    }
  };

  const handleNext = () => {
    scrollToTop();
    onNext();
  };

  const handlePrev = () => {
    scrollToTop();
    onPrev();
  };

  const formattedSelectedItems = useMemo(() => {
    const allIds = Object.keys(selectedItems);

    let validIds = allIds.filter(id => selectedItems[id]);

    // If the ids are String Numbers convert them to Numbers, only if they are not NaN
    if (validIds.some(id => !Number.isNaN(Number(id)))) {
      validIds = validIds.map(id => Number(id));
    }

    return items.filter(item => validIds.includes(item.id));
  }, [selectedItems, items]);

  // Toggle the selection of an asset
  const handleSelectionChange = useCallback(
    assetId => {
      if (selectionDisabled) {
        return;
      }
      setSelectedItems(prevSelected => ({
        ...prevSelected,
        [assetId]: !prevSelected[assetId]
      }));
    },
    [setSelectedItems, selectionDisabled]
  );

  useEffect(() => {
    if (selectionDisabled) {
      setSelectedItems({});
    }
  }, [selectionDisabled]);

  // Create a computed boolean to determine if any items are selected
  const anySelected = useMemo(
    () =>
      // Check if some items have true and the object is not empty
      Object.keys(selectedItems).some(key => selectedItems[key]),
    [selectedItems]
  );

  const fileTypeFilterLabel = fileTypeOptions.find(option => option.key === fileTypeFilter)?.text;
  const assetCategoryFilterLabel = assetCategoryOptions.find(option => option.key === assetCategoryFilter)?.text;

  // Sort switchable workspaces: is_default first, then others.
  const sortedWorkspaces = useMemo(() => {
    if (!workspaces || workspaces.length === 0) return [];
    const selectableWorkspaces = workspaces.filter(isWorkspaceSelectable);
    const defaultWorkspaces = selectableWorkspaces.filter(w => w.is_default);
    const otherWorkspaces = selectableWorkspaces.filter(w => !w.is_default);
    return [...defaultWorkspaces, ...otherWorkspaces];
  }, [workspaces]);

  // Workspace options for dropdown
  const workspaceOptions = useMemo(() => {
    return sortedWorkspaces.map(workspace => ({
      key: workspace.id,
      text: workspace.name || workspace.id
    }));
  }, [sortedWorkspaces]);

  const selectedWorkspaceForDropdown = useMemo(() => {
    if (selectedWorkspace && isWorkspaceSelectable(selectedWorkspace)) {
      return selectedWorkspace;
    }

    return sortedWorkspaces[0] || null;
  }, [selectedWorkspace, sortedWorkspaces]);

  const handleWorkspaceChange = useCallback(
    (_, data) => {
      const workspaceId = data.optionValue;
      const workspace = sortedWorkspaces.find(w => w.id === workspaceId);
      if (workspace) {
        setSelectedWorkspace(workspace);
      }
    },
    [sortedWorkspaces, setSelectedWorkspace]
  );

  const handleError = (e, item) => {
    e.target.style.display = "none";
    setFailedImages(prev => ({ ...prev, [item.id]: true }));
  };

  const intl = useIntl();

  const handleRename = (e, item) => {
    e.stopPropagation();
    setAssetToRename(item);
  };

  const handleDelete = (e, item) => {
    e.stopPropagation();
    setAssetToDelete(item);
  };

  const handleEdit = useCallback(
    (e, item) => {
      e.stopPropagation();
      initEditMode(item);
      openFileUploadDrawer();
    },
    [initEditMode, openFileUploadDrawer]
  );

  const handleAddToLibraryClick = useCallback(
    (e, item) => {
      e.stopPropagation();
      onAddToLibrary?.(item);
    },
    [onAddToLibrary]
  );

  const handlePlace = () => {
    if (selectionDisabled) {
      return;
    }

    const allIds = Object.keys(selectedItems);

    // Filter keys where the corresponding value is true
    const idsToPlace = allIds.filter(id => selectedItems[id]);
    const normalizedIds = new Set(idsToPlace.map(id => id.toString()));

    const itemsToPlace = items.filter(item => normalizedIds.has(item.id?.toString()));

    onSelectAssets(itemsToPlace);
    setSelectedItems({});

    // Close the sidebar on mobile when placing items
    // since the sidebar occupies the entire screen
    // and the user can't see the items being placed
    if (isMobile) {
      onClose();
    }
  };

  return (
    <Sidebar
      ref={drawerBodyRef}
      open={open}
      onClose={onClose}
      title={<FormattedMessage id="asset-editor.title" defaultMessage="Assets" />}
      className={styles.sidebar} // Pass the custom class to set the width
      position="end"
      footer={
        <Button
          icon={placeLoading ? <Spinner size="tiny" /> : undefined}
          appearance="primary"
          onClick={handlePlace}
          disabled={!anySelected || placeLoading || selectionDisabled}
        >
          <FormattedMessage id="object-url-modal.create-object-button" defaultMessage="Place" />
        </Button>
      }
      footerClassName={styles.footer}
      contentClassName={styles.content}
    >
      <aside>
        <div className={styles.tabListContainer}>
          <TabList
            selectedValue={selectedTab}
            onTabSelect={(e, data) => {
              setSelectedItems({});
              handleSelectedTabChange(data.value);
            }}
            className={styles.tabList}
          >
            {tabs.map(tab => (
              <Tab className={styles.tab} key={tab.value} value={tab.value}>
                {tab.label}
              </Tab>
            ))}
          </TabList>
          <Divider />
        </div>

        {/* Workspace dropdown - only show for files tab when more than one workspace is available */}
        {selectedTab === "files" && workspaceOptions.length > 1 && (
          <>
            <div className={styles.fileTypeFilterContainer}>
              <Label className={styles.fileTypeFilterLabel}>
                <FormattedMessage id="asset-editor.workspace-filter" defaultMessage="Workspace" />
              </Label>
              <Dropdown
                appearance="filled-darker"
                selectedKey={selectedWorkspaceForDropdown?.id}
                onOptionSelect={handleWorkspaceChange}
                className={styles.fileTypeFilter}
                placeholder={intl.formatMessage({
                  id: "asset-editor.workspace-filter",
                  defaultMessage: "Workspace"
                })}
                selectedOptions={selectedWorkspaceForDropdown?.id ? [selectedWorkspaceForDropdown.id] : []}
                value={selectedWorkspaceForDropdown?.name || ""}
              >
                {workspaceOptions.map(option => (
                  <Option key={option.key} value={option.key}>
                    {option.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <Divider style={{ marginBottom: tokens.spacingVerticalL }} />
          </>
        )}

        <div className={styles.searchContainer}>
          <SearchBox
            value={searchQuery}
            onChange={(_, data) => setSearchQuery(data.value)}
            placeholder="Search"
            className={styles.search}
            appearance="filled-darker"
          />
        </div>

        {/* Source dropdown only rendered if sourceOptions is not empty */}
        {sourceOptions?.length > 0 && (
          <div className={styles.fileTypeFilterContainer}>
            <Label className={styles.fileTypeFilterLabel}>
              <FormattedMessage id="asset-editor.source-filter" defaultMessage="Source" />
            </Label>
            <Dropdown
              appearance="filled-darker"
              selectedKey={source}
              onOptionSelect={handleSourceChange}
              className={styles.fileTypeFilter}
              placeholder={intl.formatMessage({ id: "asset-editor.source-filter", defaultMessage: "Source" })}
              selectedOptions={[source]}
              value={sourceOptions.find(option => option.key === source)?.text}
            >
              {sourceOptions.map(option => (
                <Option key={option.key} value={option.key}>
                  {option.text}
                </Option>
              ))}
            </Dropdown>
          </div>
        )}

        {/* FileTypeFilter and Asset Category filters - side by side for files tab */}
        {selectedTab === "files" ? (
          <div className={styles.filtersRow}>
            <div className={styles.filterColumn}>
              <Label className={styles.fileTypeFilterLabel}>
                <FormattedMessage id="asset-editor.file-type-filter" defaultMessage="Type" />
              </Label>
              <Dropdown
                appearance="filled-darker"
                selectedKey={fileTypeFilter}
                onOptionSelect={handleFileTypeFilterChange}
                className={styles.fileTypeFilter}
                placeholder={intl.formatMessage({ id: "asset-editor.file-type-filter", defaultMessage: "Type" })}
                selectedOptions={[fileTypeFilter]}
                value={fileTypeFilterLabel}
                inlinePopup
                style={{ zIndex: 10 }}
              >
                {fileTypeOptions.map(option => (
                  <Option key={option.key} value={option.key}>
                    {option.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.filterColumn}>
              <Label className={styles.fileTypeFilterLabel}>
                <FormattedMessage id="asset-editor.asset-category-filter" defaultMessage="Category" />
              </Label>
              <Dropdown
                appearance="filled-darker"
                selectedKey={assetCategoryFilter}
                onOptionSelect={handleAssetCategoryFilterChange}
                className={styles.fileTypeFilter}
                placeholder={intl.formatMessage({
                  id: "asset-editor.asset-category-filter",
                  defaultMessage: "Category"
                })}
                selectedOptions={[assetCategoryFilter]}
                value={assetCategoryFilterLabel}
                inlinePopup
                style={{ zIndex: 10 }}
              >
                {assetCategoryOptions.map(option => (
                  <Option key={option.key} value={option.key}>
                    {option.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
          </div>
        ) : (
          <div className={styles.fileTypeFilterContainer}>
            <Label className={styles.fileTypeFilterLabel}>
              <FormattedMessage id="asset-editor.file-type-filter" defaultMessage="Type" />
            </Label>
            <Dropdown
              appearance="filled-darker"
              selectedKey={fileTypeFilter}
              onOptionSelect={handleFileTypeFilterChange}
              className={styles.fileTypeFilter}
              placeholder={intl.formatMessage({ id: "asset-editor.file-type-filter", defaultMessage: "Type" })}
              selectedOptions={[fileTypeFilter]}
              value={fileTypeFilterLabel}
              inlinePopup
              style={{ zIndex: 10 }}
            >
              {fileTypeOptions.map(option => (
                <Option key={option.key} value={option.key}>
                  {option.text}
                </Option>
              ))}
            </Dropdown>
          </div>
        )}
        {loading && (
          <div>
            <div className={styles.gridContainer}>
              {[...Array(8)].map((_, index) => (
                <Skeleton key={index}>
                  <div className={styles.card}>
                    <SkeletonItem shape="rectangle" className={styles.cardPreview} style={{ height: "200px" }} />

                    <div className={styles.firstRow}>
                      <SkeletonItem shape="rectangle" style={{ width: "70%", height: "16px" }} />
                      {/* <SkeletonItem shape="circle" size={24} /> */}
                    </div>
                  </div>
                </Skeleton>
              ))}
            </div>
          </div>
        )}
        {error && (
          <p>
            <FormattedMessage
              id="asset-editor.error-loading-assets"
              defaultMessage="There was an error loading the assets."
            />
          </p>
        )}
        {!loading && !error && (
          // Add search bar
          <div>
            <div className={styles.gridContainer}>
              {items.map((item, index) => (
                <DraggableCard
                  key={`asset-${item.id}-${index}`}
                  item={item}
                  index={index}
                  selectedItems={selectedItems}
                  formattedSelectedItems={formattedSelectedItems}
                  handleSelectionChange={handleSelectionChange}
                  editable={editable}
                  canDeleteAssets={canDeleteAssets}
                  handleRename={handleRename}
                  handleDelete={handleDelete}
                  handleEdit={handleEdit}
                  handleOptimize={onOptimize}
                  canAddToLibrary={canAddToLibrary}
                  handleAddToLibrary={handleAddToLibraryClick}
                  processThumbnailUrl={processThumbnailUrl}
                  failedImages={failedImages}
                  handleError={handleError}
                  selectionDisabled={selectionDisabled}
                />
              ))}
            </div>
            {(hasNext || hasPrev) && (
              <div className={styles.pager}>
                <Button disabled={!hasPrev} onClick={handlePrev} preset="primary" appearance="transparent">
                  <ArrowLeft24Filled />
                </Button>

                <Button disabled={!hasNext} onClick={handleNext} preset="primary" appearance="transparent">
                  <ArrowRight24Filled />
                </Button>
              </div>
            )}
          </div>
        )}
        {children}
        {assetToRename && (
          <RenameAssetDialog
            asset={assetToRename}
            open={!!assetToRename}
            onOpenChange={() => setAssetToRename(false)}
            onSave={item => {
              onRename(item);
              setAssetToRename(false);
            }}
            title={intl.formatMessage({
              id: "asset-editor.rename-asset-button",
              defaultMessage: "Rename asset"
            })}
          />
        )}
        {assetToDelete && (
          <DeleteAssetDialog
            asset={assetToDelete}
            open={!!assetToDelete}
            onOpenChange={() => setAssetToDelete(false)}
            onDelete={item => {
              onDelete(item);
              setAssetToDelete(false);
            }}
            title={intl.formatMessage({
              id: "asset-editor.delete-asset-button",
              defaultMessage: "Delete asset"
            })}
          />
        )}
      </aside>
    </Sidebar>
  );
}

AssetsSidebar.propTypes = {
  children: PropTypes.node,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  items: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  processThumbnailUrl: PropTypes.func,
  searchQuery: PropTypes.string,
  setSearchQuery: PropTypes.func,
  fileTypeFilter: PropTypes.string,
  handleFileTypeFilterChange: PropTypes.func,
  fileTypeOptions: PropTypes.array,
  assetCategoryFilter: PropTypes.string,
  handleAssetCategoryFilterChange: PropTypes.func,
  assetCategoryOptions: PropTypes.array,
  onSelectAssets: PropTypes.func,
  onRename: PropTypes.func,
  onDelete: PropTypes.func,
  onOptimize: PropTypes.func,
  onAddToLibrary: PropTypes.func,
  canAddToLibrary: PropTypes.bool,
  tabs: PropTypes.array,
  selectedTab: PropTypes.string,
  handleSelectedTabChange: PropTypes.func,
  sourceOptions: PropTypes.array,
  handleSourceChange: PropTypes.func,
  source: PropTypes.string,
  hasPrev: PropTypes.bool,
  hasNext: PropTypes.bool,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  editable: PropTypes.bool,
  canDeleteAssets: PropTypes.bool,
  placeLoading: PropTypes.bool,
  initEditMode: PropTypes.func,
  openFileUploadDrawer: PropTypes.func,
  onClearEdit: PropTypes.func,
  selectionDisabled: PropTypes.bool
};
